import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useFahrzeitToBedarf, useMatchingBedarfe } from '../../../api/bewerber';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import type { MatchingBedarf, MatchingBedarfeParams } from '../../../types/bewerber';
import {
  MATCHING_BEDARFE_PAGE_SIZE,
  MAX_ROUTE_DISTANCE_KM,
} from '../../../types/bewerber';
import { GeoPrecisionConfig } from '../../../types/kennenlernen';
import type { VgAnswers } from '../../../types/vorstellungsgespraech';
import { BedarfStatusBadge } from '../../bedarfe/BedarfStatusBadge';

interface BedarfeInReichweiteSectionProps {
  bewerberId: string;
  /** Read live from the draft: the time window has no column to read it from. */
  answers: VgAnswers;
  /** A completed interview shows the list, but nothing may be triggered from it. */
  disabled?: boolean;
  /**
   * Show only the first few rows until the interviewer asks for the rest.
   * Used where the list stands in the page flow and would push everything
   * below it out of reach — the detail page tile already caps its own height.
   */
  collapsible?: boolean;
}

/**
 * Enough to see that there are matches and what the best one looks like,
 * short enough that the questions after it stay on screen.
 */
const COLLAPSED_COUNT = 3;

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const asTime = (value: unknown): string | undefined =>
  typeof value === 'string' && TIME_PATTERN.test(value) ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const formatNumber = (value: number): string =>
  value.toLocaleString('de-DE', { maximumFractionDigits: 1 });

/**
 * "Mo–Fr 08:00–13:00" for a full week, "Mo/Mi/Fr 08:00–13:00" when the same
 * hours fall on some days only, otherwise day by day. The range form is
 * reserved for the full week on purpose — "Mo–Fr" for a Monday/Wednesday/Friday
 * Bedarf would claim two days of support that are not there.
 */
function formatTimes(times: MatchingBedarf['times']): string {
  if (times.length === 0) return 'Keine Zeiten hinterlegt';

  const [first] = times;
  const uniform = times.every((t) => t.start === first.start && t.end === first.end);
  const hours = `${first.start}–${first.end}`;

  if (uniform && times.length === 5) return `Mo–Fr ${hours}`;
  if (uniform) return `${times.map((t) => t.day).join('/')} ${hours}`;

  return times.map((t) => `${t.day} ${t.start}–${t.end}`).join(' · ');
}

/**
 * The Bedarfe a candidate could actually take, judged against the answers given
 * moments earlier in the same interview. Near misses stay in the list and say
 * what is off, so the interviewer can ask about it while still on the call.
 */
export function BedarfeInReichweiteSection({
  bewerberId,
  answers,
  disabled = false,
  collapsible = false,
}: BedarfeInReichweiteSectionProps) {
  const [limit, setLimit] = useState(MATCHING_BEDARFE_PAGE_SIZE);
  const [expanded, setExpanded] = useState(false);
  const [routingId, setRoutingId] = useState<string | null>(null);

  const fahrzeit = useFahrzeitToBedarf();

  // Memoised on the answer values themselves: a fresh object every render would
  // restart the debounce timer forever.
  const criteria = useMemo<MatchingBedarfeParams>(
    () => ({
      zeitVon: asTime(answers.zeitfensterVon),
      zeitBis: asTime(answers.zeitfensterBis),
      maxPendelKm: asNumber(answers.maxPendelKm),
      maxPendelMinuten: asNumber(answers.maxPendelMinuten),
      minWochenstunden: asNumber(answers.minWochenstunden),
      maxWochenstunden: asNumber(answers.maxWochenstunden),
    }),
    [
      answers.zeitfensterVon,
      answers.zeitfensterBis,
      answers.maxPendelKm,
      answers.maxPendelMinuten,
      answers.minWochenstunden,
      answers.maxWochenstunden,
    ],
  );

  const debouncedCriteria = useDebouncedValue(criteria);
  const params = useMemo(
    () => ({ ...debouncedCriteria, limit }),
    [debouncedCriteria, limit],
  );

  const { data, isLoading, error } = useMatchingBedarfe(bewerberId, params);

  const bedarfe = data?.data ?? [];
  const meta = data?.meta;

  const isCollapsed = collapsible && !expanded;
  const visibleBedarfe = isCollapsed ? bedarfe.slice(0, COLLAPSED_COUNT) : bedarfe;
  // Collapsed the count comes from the server total, because the hidden rows
  // are not only the ones already loaded.
  const hiddenCount = (meta?.total ?? bedarfe.length) - visibleBedarfe.length;

  const handleFahrzeit = (bedarfId: string) => {
    setRoutingId(bedarfId);
    fahrzeit.mutate(
      { bewerberId, bedarfId },
      {
        onError: (err: unknown) => {
          const message = (err as { response?: { data?: { message?: string | string[] } } })
            ?.response?.data?.message;
          toast.error(
            Array.isArray(message)
              ? message.join(', ')
              : message || 'Fahrzeit konnte nicht berechnet werden',
          );
        },
        onSettled: () => setRoutingId(null),
      },
    );
  };

  const criteriaSummary = [
    meta ? `Umkreis ${formatNumber(meta.radiusKm)} km` : null,
    criteria.zeitVon && criteria.zeitBis
      ? `Zeitfenster ${criteria.zeitVon}–${criteria.zeitBis}`
      : null,
    criteria.maxPendelMinuten ? `max. ${criteria.maxPendelMinuten} Min. Fahrzeit` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (isLoading && !data) {
    return <p className="text-sm text-gray-500">Bedarfe werden gesucht…</p>;
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">Bedarfe konnten nicht geladen werden.</p>
    );
  }

  if (meta && !meta.bewerberGeocoded) {
    return (
      <p className="text-sm text-amber-700">
        Die Adresse des Bewerbers wird noch verortet. Die Liste steht in wenigen Minuten
        zur Verfügung.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>{criteriaSummary}</span>
        {meta && (
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary-100 text-primary-800 font-medium">
            {meta.total} gefunden
          </span>
        )}
      </div>

      {meta && !meta.zeitfensterActive && (
        <p className="mt-1 text-xs text-amber-700">
          Keine Zeiten erfasst — es wird nur nach Entfernung gesucht.
        </p>
      )}

      {meta && meta.hiddenRouteTime > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          {meta.hiddenRouteTime} Bedarf{meta.hiddenRouteTime === 1 ? '' : 'e'} wegen zu
          langer Fahrzeit ausgeblendet.
        </p>
      )}

      {meta && meta.hiddenNoTimes > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          {meta.hiddenNoTimes} Bedarf{meta.hiddenNoTimes === 1 ? '' : 'e'} ohne
          hinterlegte Zeiten ausgeblendet.
        </p>
      )}

      {bedarfe.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          Kein passender Bedarf in Reichweite.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {visibleBedarfe.map((bedarf) => {
            const precision = GeoPrecisionConfig[bedarf.geoPrecision];
            const isClean = bedarf.issues.length === 0;
            // Normally the list already carries the travel time. The button is
            // the fallback for the rows the local routing graph could not answer
            // — outside NRW, or while the container was down. Beyond the routing
            // limit the backend refuses, so it is not offered there at all.
            const canCalculateFahrzeit =
              !disabled &&
              bedarf.geoPrecision !== 'NONE' &&
              bedarf.routeDurationMin === null &&
              bedarf.distanceKm <= MAX_ROUTE_DISTANCE_KM;

            return (
              <li key={bedarf.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/bedarfe/${bedarf.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                      >
                        {bedarf.childFirstName} {bedarf.childLastName ?? ''}
                      </Link>
                      {/* No "Passt" badge: being in this list already means the
                          Bedarf fits. Only a reservation is worth a label — and
                          those are the amber ones further down. */}
                      <BedarfStatusBadge status={bedarf.status} size="sm" />
                    </div>

                    <p className="mt-0.5 text-xs text-gray-500 truncate">
                      {[bedarf.schoolName, bedarf.schoolCity].filter(Boolean).join(' · ') ||
                        'Keine Schule hinterlegt'}
                    </p>

                    <p className="mt-0.5 text-xs text-gray-600">
                      {formatTimes(bedarf.times)}
                      {bedarf.weeklyHours > 0 &&
                        ` · ${formatNumber(bedarf.weeklyHours)} Std./Woche`}
                    </p>

                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                      {/* The driving time leads: it is what the candidate
                          actually commutes, and it is now there for every row
                          the routing graph covers. */}
                      {bedarf.routeDurationMin !== null ? (
                        <>
                          <span className="font-medium text-gray-900">
                            {bedarf.routeDurationMin} Min. Fahrt
                          </span>
                          <span>
                            {formatNumber(bedarf.routeDistanceKm ?? 0)} km ·{' '}
                            {formatNumber(bedarf.distanceKm)} km Luftlinie
                          </span>
                        </>
                      ) : (
                        <span className="font-medium">
                          {formatNumber(bedarf.distanceKm)} km Luftlinie
                        </span>
                      )}
                      {/* An exactly located address is the normal case; saying so
                          on every row is noise. Only doubt is worth a chip. */}
                      {bedarf.geoPrecision !== 'EXACT' && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded font-medium ${precision.className}`}
                          title={precision.description}
                        >
                          {precision.label}
                        </span>
                      )}
                    </div>

                    {!isClean && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {bedarf.issues.map((issue) => (
                          <span
                            key={issue.kind}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                          >
                            {issue.text}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {canCalculateFahrzeit && (
                    <button
                      type="button"
                      onClick={() => handleFahrzeit(bedarf.id)}
                      disabled={routingId !== null}
                      title="Diese Strecke konnte nicht automatisch berechnet werden – einmalig nachholen"
                      className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {routingId === bedarf.id ? 'Berechne…' : 'Fahrzeit nachholen'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        Collapsed, one button opens the list; open, one closes it again. The
        pagination button only makes sense in the open state — it would
        otherwise load rows the collapsed list does not show.
      */}
      {isCollapsed && hiddenCount > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Alle {meta?.total ?? bedarfe.length} Bedarfe anzeigen ({hiddenCount} weitere)
          </button>
        </div>
      )}

      {!isCollapsed && (
        <div className="mt-3 flex flex-wrap gap-2">
          {meta && bedarfe.length < meta.total && (
            <button
              type="button"
              onClick={() => setLimit((current) => current + MATCHING_BEDARFE_PAGE_SIZE)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Weitere {MATCHING_BEDARFE_PAGE_SIZE} anzeigen ({meta.total - bedarfe.length}{' '}
              übrig)
            </button>
          )}

          {collapsible && bedarfe.length > COLLAPSED_COUNT && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Weniger anzeigen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
